import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import {
  ArrowLeft,
  Heart,
  MessageCircle,
  Shield,
  Users,
  Phone,
  Globe,
  ExternalLink,
  AlertTriangle
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FamilyResources = () => {
  const navigate = useNavigate();
  const [resources, setResources] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadResources();
  }, []);

  const loadResources = async () => {
    try {
      const response = await axios.get(`${API}/family/resources`);
      setResources(response.data);
    } catch (error) {
      console.error("Failed to load family resources:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const getIcon = (iconName) => {
    switch (iconName) {
      case "heart": return <Heart className="w-5 h-5" />;
      case "message-circle": return <MessageCircle className="w-5 h-5" />;
      case "shield": return <Shield className="w-5 h-5" />;
      case "users": return <Users className="w-5 h-5" />;
      default: return <Heart className="w-5 h-5" />;
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading resources...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-blue-700">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold" style={{ fontFamily: 'Manrope' }}>
                  Family Resources
                </h1>
                <p className="text-sm text-blue-100">
                  Supporting those who support our veterans
                </p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-blue-50 border-b border-blue-100 px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <Users className="w-12 h-12 text-blue-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-blue-900 mb-2">
            You're Not Alone in This Journey
          </h2>
          <p className="text-blue-700 max-w-2xl mx-auto">
            Supporting a veteran can be challenging. These resources are here to help you 
            understand, communicate, and take care of yourself along the way.
          </p>
        </div>
      </div>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Crisis Note */}
        {resources?.crisis_note && (
          <Card className="card-valor mb-8 border-red-200 bg-red-50">
            <CardContent className="p-4 flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">{resources.crisis_note}</p>
            </CardContent>
          </Card>
        )}

        {/* Resource Categories */}
        {resources?.categories?.map((category) => (
          <Card key={category.id} className="card-valor mb-6" data-testid={`category-${category.id}`}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <span className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                  {getIcon(category.icon)}
                </span>
                {category.title}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Accordion type="single" collapsible className="w-full">
                {category.resources.map((resource, idx) => (
                  <AccordionItem key={idx} value={`item-${idx}`}>
                    <AccordionTrigger className="text-left">
                      <div>
                        <p className="font-medium">{resource.title}</p>
                        <p className="text-sm text-muted-foreground font-normal">{resource.description}</p>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="pt-2 space-y-2">
                        {resource.content && (
                          <ul className="space-y-2">
                            {resource.content.map((item, i) => (
                              <li key={i} className="flex items-start gap-2">
                                <span className="w-1.5 h-1.5 rounded-full bg-primary mt-2 flex-shrink-0" />
                                <span className="text-sm">{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {resource.tips && (
                          <ul className="space-y-2">
                            {resource.tips.map((tip, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <span className="text-green-600">✓</span>
                                <span>{tip}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {resource.avoid && (
                          <ul className="space-y-2">
                            {resource.avoid.map((item, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-red-700">
                                <span>✗</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                        {resource.signs && (
                          <ul className="space-y-2">
                            {resource.signs.map((sign, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm">
                                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                                <span>{sign}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </CardContent>
          </Card>
        ))}

        {/* Organizations */}
        {resources?.organizations && (
          <Card className="card-valor mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="w-5 h-5 text-primary" />
                Helpful Organizations
              </CardTitle>
              <CardDescription>
                Professional support for military families
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {resources.organizations.map((org, idx) => (
                  <div key={idx} className="p-4 bg-muted/50 rounded-lg">
                    <div className="flex items-start justify-between">
                      <div>
                        <h4 className="font-semibold">{org.name}</h4>
                        <p className="text-sm text-muted-foreground mt-1">{org.description}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          {org.phone && (
                            <a href={`tel:${org.phone.replace(/\D/g, '')}`}>
                              <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                                <Phone className="w-3 h-3 mr-1" />
                                {org.phone}
                              </Badge>
                            </a>
                          )}
                          {org.url && (
                            <a href={org.url} target="_blank" rel="noopener noreferrer">
                              <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                                <Globe className="w-3 h-3 mr-1" />
                                Website
                                <ExternalLink className="w-3 h-3 ml-1" />
                              </Badge>
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Back to Home */}
        <div className="text-center mt-8">
          <Button variant="outline" onClick={() => navigate("/")}>
            Back to Home
          </Button>
        </div>
      </main>
    </div>
  );
};

export default FamilyResources;
